import { getWorkspaceSubscriptionStatusFunction } from '@easy-expense/auth-client/src/Callable.backend';
import { useCurrentWorkspace, useSubscriptionStore } from '@easy-expense/data-firestore-client';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { PlanGateContext, PlanGatePrompt, PlanGateValue } from './PlanGate.context';
import { UpgradeModal } from './UpgradeModal.component';
import { onBillingChanged } from '../../lib/Billing/billingChanged';
import { useDisablePaywall } from '../../lib/disablePaywall';

/** An open wall, with the page it was opened on. */
type OpenPrompt = PlanGatePrompt & { pathname: string };

/**
 * Owns the one upgrade modal for the web app, so every metered action decides the same way and
 * no screen mounts its own wall. Plan flags and the seat allocation come from the shared
 * subscription store, which useListenToSubscription keeps current per workspace.
 *
 * The provider sits above the routes, so a wall is tied to the page that opened it: leaving that
 * page (browser Back included) closes it without running its onDismiss, which belongs to the page
 * that is gone -- RequirePlan's would otherwise redirect the new page to transactions.
 */
export const PlanGateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const hasSubscription = useSubscriptionStore((s) => s.hasSubscription);
  const hasProPlanSubscription = useSubscriptionStore((s) => s.hasProPlanSubscription);
  const seatsUsed = useSubscriptionStore((s) => s.seatsUsed);
  const seatsAllowed = useSubscriptionStore((s) => s.seatsAllowed);
  const seatsLoaded = useSubscriptionStore((s) => s.seatsLoaded);
  const [prompt, setPrompt] = React.useState<OpenPrompt | null>(null);
  const { pathname } = useLocation();
  // Read at call time, not render time: a gated route opens its wall from its first effect,
  // before this provider's effects see the new page.
  const pathnameRef = React.useRef(pathname);
  pathnameRef.current = pathname;
  const currentWorkspace = useCurrentWorkspace();
  const disablePaywall = useDisablePaywall();

  // useListenToSubscription only re-checks coverage when the workspace changes (or, on mobile,
  // when the app returns to the foreground), so a seat purchase or plan change made in billing
  // settings would otherwise keep gating invites on the old allocation until a page reload.
  React.useEffect(() => {
    const workspaceID = currentWorkspace?.key;
    if (!workspaceID) {
      return;
    }
    let cancelled = false;
    const unsubscribe = onBillingChanged(() => {
      getWorkspaceSubscriptionStatusFunction()({ workspaceID })
        .then((response) => {
          if (cancelled) {
            return;
          }
          useSubscriptionStore.getState().setCoverage({
            coveringUserID: response.data.coveringUserID,
            coverageSubscriptionID: response.data.subscriptionID ?? null,
            coverageActive: response.data.active,
            coverageTier: response.data.active ? response.data.tier : null,
            seatsUsed: response.data.seatsUsed,
            seatsAllowed: response.data.seatsAllowed,
          });
        })
        .catch((error) => {
          console.warn('[PlanGateProvider] Failed to refresh workspace coverage', error);
        });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentWorkspace?.key]);

  // Turning on staging's Disable Paywall takes down a wall that is already open, without its
  // onDismiss: the action is now allowed, so RequirePlan must not redirect away.
  React.useEffect(() => {
    if (disablePaywall) {
      setPrompt(null);
    }
  }, [disablePaywall]);

  React.useEffect(() => {
    setPrompt((open) => {
      if (open && open.pathname !== pathname) {
        return null;
      }
      return open;
    });
  }, [pathname]);

  const value = React.useMemo<PlanGateValue>(
    () => ({
      hasSubscription,
      hasProPlanSubscription,
      seats: { hasLoaded: seatsLoaded, seatsUsed, seatsAllowed },
      showPaywall: (newPrompt: PlanGatePrompt) =>
        setPrompt({ ...newPrompt, pathname: pathnameRef.current }),
    }),
    [hasSubscription, hasProPlanSubscription, seatsLoaded, seatsUsed, seatsAllowed],
  );

  return (
    <PlanGateContext.Provider value={value}>
      {children}
      {prompt ? (
        <UpgradeModal
          action={prompt.action}
          decision={prompt.decision}
          seats={value.seats}
          usage={prompt.usage}
          onClose={() => {
            setPrompt(null);
            prompt.onDismiss?.();
          }}
        />
      ) : null}
    </PlanGateContext.Provider>
  );
};
